import React, { useState } from 'react'
import { makeStyles, TableCell } from '@material-ui/core'
import { ProviderFragment } from '../../../generated/graphql'
import { formatPercent } from '../../../util/formatters'
import { UsageMatrixConnectionDialog } from './UsageMatrixConnectionDialog'
import { SpecialDemand } from '../../../calculators/special-demand-ids'

const useStyles = makeStyles((theme) => ({
  providerDemandCell: {
    textAlign: 'center',
  },
  providerDemandCellTogglable: {
    cursor: 'pointer',
    '&:hover': {
      background: theme.palette.grey[100],
    },
  },
}))

export type MatrixDemand = {
  id: string
  name: string
  isToggleable: boolean
  isEditable: boolean
}

type Props = {
  provider: ProviderFragment
  demand: MatrixDemand
  formatProvidedEnergyOrPower: (
    providerId: string,
    demandId: string,
    isConnected: boolean,
  ) => React.ReactNode
  onToggleAllocation?: (provider: ProviderFragment, demandId: string) => void
  onEditAllocation?: (
    provider: ProviderFragment,
    demandId: string,
    maxPortion: number,
  ) => void
  isEditing: boolean
}

export function UsageMatrixConnectionCell({
  provider,
  demand,
  formatProvidedEnergyOrPower,
  onToggleAllocation,
  onEditAllocation,
  isEditing,
}: Props): React.ReactElement {
  const classes = useStyles()
  const [editOpen, setEditOpen] = useState(false)

  const resolveMaxPortion = () => {
    if (demand.id === SpecialDemand.OtherProvidersElectricity) {
      return provider.providesElectricityForProviders ? 1 : 0
    }

    return (
      provider.demands.find(
        (providerDemand) => providerDemand.demand.id === demand.id,
      )?.maxPortion ?? 0
    )
  }

  const maxPortion = resolveMaxPortion()
  const connected = maxPortion !== 0

  const formattedValue = isEditing
    ? formatPercent(maxPortion)
    : formatProvidedEnergyOrPower(provider.id, demand.id, connected)

  const isToggleable = !provider.isFallback && demand.isToggleable
  const isEditable = isToggleable && demand.isEditable

  const isClickable = isEditing ? isEditable : isToggleable

  return (
    <>
      <TableCell
        key={provider.id}
        className={`${classes.providerDemandCell} ${
          isClickable ? classes.providerDemandCellTogglable : ''
        }`}
        onClick={() => {
          if (isEditing) {
            if (isEditable) {
              setEditOpen(true)
            }
            return
          }

          if (isToggleable && onToggleAllocation) {
            onToggleAllocation(provider, demand.id)
          }
        }}
      >
        {formattedValue}
        {connected && maxPortion < 1 && ' *'}
      </TableCell>

      {onEditAllocation && (
        <UsageMatrixConnectionDialog
          open={editOpen}
          onClose={() => setEditOpen(false)}
          provider={provider}
          demand={demand}
          defaultValues={{ maxPortion: maxPortion.toString() }}
          onEditAllocation={onEditAllocation}
        />
      )}
    </>
  )
}
